import { HttpClient, httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { UserService } from './user.service';

export type NotificationLevel = 'Error' | 'Warning' | 'Information' | 'Promo';

export interface NotificationDto {
    id: number;
    title: string;
    message: string;
    link: string;
    notificationType: NotificationLevel;
    readAt?: string;
    createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
    private readonly http = inject(HttpClient);
    private readonly userService = inject(UserService);

    /** httpResource para carregar notificações automaticamente quando autenticado */
    readonly notificationsResource = httpResource<NotificationDto[]>(() => {
        if (!this.userService.accessToken()) return undefined;
        return `${environment.API_HOST}/Notification`;
    });

    /** Notificações com fallback para array vazio */
    readonly notifications = computed(() => this.notificationsResource.value() ?? []);

    /** Notificações não lidas */
    readonly unreadNotifications = computed(() =>
        this.notifications().filter(n => !n.readAt)
    );

    /** Contagem de não lidas */
    readonly unreadCount = computed(() => this.unreadNotifications().length);

    /** Verifica se existe alguma notificação de erro não lida */
    readonly hasUnreadError = computed(() =>
        this.unreadNotifications().filter(n => n.notificationType === 'Error')
    );

    /** Notificação ativa para exibir no modal */
    readonly activeErrorNotification = signal<NotificationDto | null>(null);

    constructor() {
        // Effect para exibir modal quando houver erro não lido
        effect(() => {
            const unreadErrors = this.hasUnreadError();
            if (unreadErrors.length) {
                this.activeErrorNotification.set(unreadErrors[0]);
            }
        });
    }

    /** Recarrega as notificações */
    reload(): void {
        this.notificationsResource.reload();
    }

    /** Marca uma notificação como lida */
    markAsRead(notificationId: number): void {
        this.http.post<NotificationDto>(`${environment.API_HOST}/Notification/${notificationId}/Read`, {})
            .subscribe(updated => {
                setTimeout(() => {
                    this.notificationsResource.update(current =>
                        current?.map(n => n.id === updated.id ? updated : n)
                    );
                }, 500);
            });
    }

    /** Marca todas as notificações como lidas */
    markAllAsRead(): void {
        this.http.post<NotificationDto[]>(`${environment.API_HOST}/Notification/ReadAllUnreaded`, {})
            .subscribe(() => this.notificationsResource.reload());
    }

    /** Exibe uma notificação específica no modal */
    showNotificationModal(notification: NotificationDto): void {
        this.activeErrorNotification.set(notification);
    }

    /** Fecha o modal de notificação e marca como lida */
    dismissNotificationModal(markAsRead = true): void {
        const current = this.activeErrorNotification();
        this.activeErrorNotification.set(null);

        if (current && markAsRead) {
            this.markAsRead(current.id);
        }
    }

    /** Reseta o estado (usado no logout) */
    reset(): void {
        this.activeErrorNotification.set(null);
    }
};
